import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import { PageTemplate } from '@/pwa/templates/PageTemplate/PageTemplate';
import { PageHeader } from '@/pwa/atoms/PageHeader/PageHeader';
import DataTable, { GridColDef } from '@/pwa/organisms/DataTable/DataTable';

export interface CodeSummary {
  totalOrganisationCreationCodes: number;
  redeemedOrganisationCreationCodes: number;
  totalLicenceCodes: number;
  redeemedLicenceCodes: number;
}

export interface OrganisationCreationCode {
  id: string;
  code: string;
  orgOwnerEmail?: string;
  status: 'available' | 'redeemed';
  redeemedAt?: string;
  createdAt: string;
}

export interface LicenceCode {
  id: string;
  code: string;
  organisationCreationCode: string;
  startDate?: string;
  expiresAt?: string;
  isRedeemed: boolean;
  redeemedAt?: string;
  createdAt: string;
}

export interface ImportResult {
  createdOrganisationCreationCodes: number;
  skippedOrganisationCreationCodes: number;
  createdLicenceCodes: number;
  updatedLicenceCodes: number;
  skippedLicenceCodes: number;
  errors: { row: number; message: string }[];
}

export interface PreviewRow {
  organisationCreationCode: string;
  orgOwnerEmail?: string;
  licenceCode: string;
  startDate: string;
}

// -- Shared column helpers --

const MonoCell = ({ value }: { value: string }) => (
  <Typography
    variant='body2'
    sx={{
      fontFamily: 'monospace',
    }}
  >
    {value}
  </Typography>
);

const formatDate = (value: string) =>
  value ? new Date(value).toLocaleDateString() : '—';

const StatusChip = ({
  label,
  isAvailable,
}: {
  label: string;
  isAvailable: boolean;
}) => (
  <Chip
    label={label}
    color={isAvailable ? 'success' : 'default'}
    size='small'
  />
);

// -- Column definitions --

const getEligibility = (row: LicenceCode) => {
  if (!row.expiresAt) return { label: 'No expiry', color: 'default' };

  const now = new Date();
  const expiry = new Date(row.expiresAt);

  if (row.startDate && new Date(row.startDate) > now)
    return {
      label: `Starts ${new Date(row.startDate).toLocaleDateString()}`,
      color: 'warning',
    };

  if (expiry <= now)
    return { label: `Expired ${expiry.toLocaleDateString()}`, color: 'error' };

  return {
    label: `Active until ${expiry.toLocaleDateString()}`,
    color: 'success',
  };
};

const orgOwnerEmailColumn: GridColDef = {
  field: 'orgOwnerEmail',
  headerName: 'Email',
  flex: 1,
  minWidth: 180,
  valueGetter: (value: string) => value || '—',
};

const getOccColumns = (
  licenceCodes: LicenceCode[],
  onCopyLink?: (code: string) => void,
): GridColDef[] => [
  {
    field: 'code',
    headerName: 'Code',
    flex: 1,
    minWidth: 150,
    renderCell: ({ value }) => <MonoCell value={value} />,
  },
  {
    field: 'redeemedByOrgName',
    headerName: 'Redeemed By',
    flex: 1,
    minWidth: 150,
    valueGetter: (value: string) => value || '—',
  },
  {
    field: 'redeemedAt',
    headerName: 'Redeemed At',
    flex: 0.7,
    minWidth: 120,
    valueGetter: formatDate,
  },
  {
    field: 'status',
    headerName: 'Status',
    flex: 0.5,
    minWidth: 100,
    renderCell: ({ value }) => (
      <StatusChip label={value} isAvailable={value === 'available'} />
    ),
  },
  {
    field: 'seatsRedeemed',
    headerName: 'Seats redeemed',
    flex: 0.6,
    minWidth: 130,
    sortable: false,
    valueGetter: (_value, row: OrganisationCreationCode) => {
      const linked = licenceCodes.filter(
        (lc) => lc.organisationCreationCode === row.code,
      );
      const redeemed = linked.filter((lc) => lc.isRedeemed).length;
      return `${redeemed}/${linked.length}`;
    },
  },
  ...(onCopyLink
    ? [
        {
          field: 'actions',
          headerName: '',
          width: 200,
          sortable: false,
          filterable: false,
          disableColumnMenu: true,
          renderCell: ({ row }: { row: OrganisationCreationCode }) =>
            row.status === 'available' ? (
              <Button
                size='small'
                variant='outlined'
                onClick={() => onCopyLink(row.code)}
              >
                Copy Invite Link
              </Button>
            ) : null,
        },
      ]
    : []),
];

const lcColumns: GridColDef[] = [
  {
    field: 'code',
    headerName: 'Code',
    flex: 1,
    minWidth: 150,
    renderCell: ({ value }) => <MonoCell value={value} />,
  },
  {
    field: 'organisationCreationCode',
    headerName: 'Registration Code',
    flex: 1,
    minWidth: 150,
    renderCell: ({ value }) => <MonoCell value={value} />,
  },
  {
    field: 'startDate',
    headerName: 'Start Date',
    flex: 0.7,
    minWidth: 120,
    valueGetter: formatDate,
  },
  {
    field: 'redeemedByOrgName',
    headerName: 'Redeemed By',
    flex: 1,
    minWidth: 150,
    valueGetter: (value: string) => value || '—',
  },
  {
    field: 'eligibility',
    headerName: 'Eligibility',
    flex: 0.7,
    minWidth: 140,
    valueGetter: (_value: unknown, row: LicenceCode) => {
      return getEligibility(row);
    },
    renderCell: ({
      value,
    }: {
      value: {
        label: string;
        color: 'success' | 'error' | 'warning' | 'default';
      };
    }) => {
      return <Chip label={value?.label} size='small' color={value?.color} />;
    },
  },
  {
    field: 'isRedeemed',
    headerName: 'Status',
    flex: 0.5,
    minWidth: 100,
    renderCell: ({ value }) => (
      <StatusChip
        label={value ? 'redeemed' : 'available'}
        isAvailable={!value}
      />
    ),
  },
  {
    field: 'redeemedAt',
    headerName: 'Redeemed At',
    flex: 0.7,
    minWidth: 120,
    valueGetter: formatDate,
  },
];

const previewColumns: GridColDef[] = [
  {
    field: 'organisationCreationCode',
    headerName: 'Registration Code',
    flex: 1,
    minWidth: 150,
  },
  {
    field: 'licenceCode',
    headerName: 'Seat Code',
    flex: 1,
    minWidth: 150,
  },
  {
    field: 'startDate',
    headerName: 'Start Date',
    flex: 0.7,
    minWidth: 120,
  },
];

// -- Sub-components --

const SummaryCard = ({
  label,
  redeemed,
  total,
}: {
  label: string;
  redeemed: number;
  total: number;
}) => (
  <Card sx={{ minWidth: 200, flex: 1 }}>
    <CardContent>
      <Typography
        variant='subtitle2'
        sx={{
          color: 'text.secondary',
        }}
      >
        {label}
      </Typography>
      <Typography variant='h4'>
        {redeemed} / {total}
      </Typography>
      <Typography
        variant='caption'
        sx={{
          color: 'text.secondary',
        }}
      >
        redeemed / total
      </Typography>
    </CardContent>
  </Card>
);

interface CSVUploadCardProps {
  onFileSelected: (e: React.ChangeEvent<HTMLInputElement>) => void;
  previewRows: PreviewRow[];
  previewFileName?: string;
  parseError?: string | null;
  onSubmit: () => void;
  onCancel: () => void;
  isParsing: boolean;
  isUploading: boolean;
  lastResult?: ImportResult | null;
  uploadError?: string | null;
  previewColumns: GridColDef[];
  isShowOrgOwnerEmail: boolean;
}

const CSVUploadCard = ({
  onFileSelected,
  previewRows,
  previewFileName,
  parseError,
  onSubmit,
  onCancel,
  isParsing,
  isUploading,
  lastResult,
  uploadError,
  previewColumns: columns,
  isShowOrgOwnerEmail,
}: CSVUploadCardProps) => {
  const showFileInput = previewRows.length === 0 && !isUploading && !isParsing;
  const previewRowsWithId = previewRows
    .slice(0, 10)
    .map((r, i) => ({ ...r, id: String(i) }));

  const [isResultDismissed, setIsResultDismissed] = useState(false);
  useEffect(() => {
    setIsResultDismissed(false);
  }, [lastResult]);

  return (
    <Card>
      <CardContent>
        <Stack spacing={2}>
          <PageHeader
            variant='h2'
            title='Import Codes (CSV)'
            description='Registration codes represent the referrers you intend to allocate seat codes to. Seat codes are the individual licences assigned to a referrer under a registration code. You may distribute your allocated amount of seat codes between as many registration codes as you would wish.'
          />
          <Alert severity='info' sx={{ alignSelf: 'flex-start' }}>
            {isShowOrgOwnerEmail
              ? 'Upload a CSV with 4 columns: registration code, org owner email, seat code, start date (YYYY-MM-DD).'
              : 'Upload a CSV with 3 columns: registration code, seat code, start date (YYYY-MM-DD).'}
          </Alert>

          {showFileInput && (
            <Box>
              <input
                type='file'
                accept='.csv'
                onChange={onFileSelected}
                style={{ display: 'none' }}
                id='csv-upload'
              />
              <label htmlFor='csv-upload'>
                <Button variant='contained' component='span'>
                  Choose CSV File
                </Button>
              </label>
            </Box>
          )}

          {isParsing && (
            <Stack
              direction='row'
              spacing={1}
              sx={{
                alignItems: 'center',
              }}
            >
              <CircularProgress size={20} />
              <Typography
                variant='body2'
                sx={{
                  color: 'text.secondary',
                }}
              >
                Parsing CSV…
              </Typography>
            </Stack>
          )}

          {parseError && <Alert severity='error'>{parseError}</Alert>}

          {previewRows.length > 0 && (
            <>
              <Typography variant='body2'>
                Preview: {previewRows.length} row(s) from{' '}
                {previewFileName ?? 'CSV'}
              </Typography>
              <DataTable
                columns={columns}
                rows={previewRowsWithId}
                isSearchable={false}
                isPaginated={false}
                isTotalRows={false}
              />
              {previewRows.length > 10 && (
                <Typography
                  variant='body2'
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  …and {previewRows.length - 10} more row(s)
                </Typography>
              )}
              <Stack direction='row' spacing={1}>
                <Button
                  variant='contained'
                  onClick={onSubmit}
                  disabled={isUploading}
                >
                  {isUploading
                    ? 'Uploading…'
                    : `Import ${previewRows.length} row(s)`}
                </Button>
                <Button
                  variant='outlined'
                  onClick={onCancel}
                  disabled={isUploading}
                >
                  Cancel
                </Button>
              </Stack>
            </>
          )}

          {uploadError && <Alert severity='error'>{uploadError}</Alert>}
          {lastResult && !isResultDismissed && (
            <Alert
              severity={
                (lastResult.errors ?? []).length > 0 ? 'warning' : 'success'
              }
              onClose={() => setIsResultDismissed(true)}
            >
              Created {lastResult.createdOrganisationCreationCodes} registration
              codes, {lastResult.createdLicenceCodes} seat codes. Updated{' '}
              {lastResult.updatedLicenceCodes}. Skipped{' '}
              {lastResult.skippedOrganisationCreationCodes +
                lastResult.skippedLicenceCodes}
              .
              {(lastResult.errors ?? []).length > 0 &&
                ` ${(lastResult.errors ?? []).length} error(s).`}
            </Alert>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

// -- Main component --

export interface PartnerAdminDashboardProps {
  summary: CodeSummary;
  organisationCreationCodes: OrganisationCreationCode[];
  licenceCodes: LicenceCode[];
  onFileSelected: (e: React.ChangeEvent<HTMLInputElement>) => void;
  previewRows: PreviewRow[];
  previewFileName?: string;
  parseError?: string | null;
  onSubmit: () => void;
  onCancel: () => void;
  isParsing?: boolean;
  isUploading?: boolean;
  lastResult?: ImportResult | null;
  uploadError?: string | null;
  isShowOrgOwnerEmail?: boolean;
  onCopySignUpLink?: (code: string) => void;
}

export const PartnerAdminDashboard = ({
  summary,
  organisationCreationCodes,
  licenceCodes,
  onFileSelected,
  previewRows,
  previewFileName,
  parseError,
  onSubmit,
  onCancel,
  isParsing = false,
  isUploading = false,
  lastResult,
  uploadError,
  isShowOrgOwnerEmail = false,
  onCopySignUpLink,
}: PartnerAdminDashboardProps) => {
  const occColumns = getOccColumns(licenceCodes, onCopySignUpLink);

  const activeOccColumns = isShowOrgOwnerEmail
    ? [
        occColumns[0],
        occColumns[1],
        orgOwnerEmailColumn,
        ...occColumns.slice(2),
      ]
    : occColumns;

  const activePreviewColumns = isShowOrgOwnerEmail
    ? [previewColumns[0], orgOwnerEmailColumn, ...previewColumns.slice(1)]
    : previewColumns;

  return (
    <PageTemplate
      maxWidth={false}
      headerSlot={
        <PageHeader
          title='Code Management'
          description='Upload and manage registration codes and seat codes.'
        />
      }
      contentSlot={
        <Stack spacing={3}>
          {/* Summary cards */}
          <Stack
            direction='row'
            spacing={2}
            sx={{
              flexWrap: 'wrap',
            }}
          >
            <SummaryCard
              label='Registration Codes'
              redeemed={summary.redeemedOrganisationCreationCodes}
              total={summary.totalOrganisationCreationCodes}
            />
            <SummaryCard
              label='Seat Codes'
              redeemed={summary.redeemedLicenceCodes}
              total={summary.totalLicenceCodes}
            />
          </Stack>

          {/* Upload */}
          <CSVUploadCard
            onFileSelected={onFileSelected}
            previewRows={previewRows}
            previewFileName={previewFileName}
            parseError={parseError}
            onSubmit={onSubmit}
            onCancel={onCancel}
            isParsing={isParsing}
            isUploading={isUploading}
            lastResult={lastResult}
            uploadError={uploadError}
            previewColumns={activePreviewColumns}
            isShowOrgOwnerEmail={isShowOrgOwnerEmail}
          />

          {/* Organisation Creation Codes table */}
          <Card>
            <CardContent>
              <Stack spacing={2}>
                <PageHeader
                  variant='h2'
                  title='Registration Codes'
                  description='Copy each invite link and send it to the referrer you wish to allocate it to. Once they sign up, they can distribute seat codes to their clients.'
                />
                <DataTable
                  columns={activeOccColumns}
                  rows={organisationCreationCodes}
                  noDataMsg='No codes imported yet.'
                  rowsPerPage={10}
                />
              </Stack>
            </CardContent>
          </Card>

          {/* Licence Codes table */}
          <Card>
            <CardContent>
              <Stack spacing={2}>
                <PageHeader
                  variant='h2'
                  title='Seat Codes'
                  description='Each seat code is one client licence linked to a registration code. Track redemption and remaining availability here.'
                />
                <DataTable
                  columns={lcColumns}
                  rows={licenceCodes}
                  noDataMsg='No seat codes imported yet.'
                  rowsPerPage={10}
                />
              </Stack>
            </CardContent>
          </Card>
        </Stack>
      }
    />
  );
};

PartnerAdminDashboard.displayName = 'PartnerAdminDashboard';
export default PartnerAdminDashboard;
